import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  FileText, MapPin, DollarSign, Clock, Edit2, Loader2, AlertCircle, CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  open: 'bg-green-50 text-green-600 border-green-200',
  matched: 'bg-blue-50 text-blue-600 border-blue-200',
  contacted: 'bg-purple-50 text-purple-600 border-purple-200',
  closed: 'bg-gray-50 text-gray-600 border-gray-200'
};

const MyRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const res = await axios.get(`${API}/requests/customer/${user.user_id}`, {
          withCredentials: true
        });
        setRequests(res.data);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchRequests();
    }
  }, [user]);

  const handleEdit = (request) => {
    setEditingId(request.id);
    setEditForm({
      description: request.description || '',
      budget_range: request.budget_range || '',
      urgency_level: request.urgency_level || 'normal'
    });
  };

  const handleSave = async (requestId) => {
    try {
      await axios.put(
        `${API}/requests/${requestId}`,
        editForm,
        { withCredentials: true }
      );
      // Refresh requests
      const res = await axios.get(`${API}/requests/customer/${user.user_id}`, {
        withCredentials: true
      });
      setRequests(res.data);
      setEditingId(null);
    } catch (e) {
      console.error('Error:', e);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Requests</h1>
        <p className="text-sm text-gray-500 mt-1">Track your service requests and manage details.</p>
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <FileText size={40} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No requests yet</h3>
          <p className="text-sm text-gray-500 mb-4">Start by searching for a service and submitting a request.</p>
          <a
            href="/"
            className="inline-block bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            Find a Professional
          </a>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const isEditing = editingId === request.id;
            return (
              <div key={request.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{request.service_name || request.service}</h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Clock size={12} /> {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusColors[request.status] || statusColors.open}`}>
                        {request.status || 'open'}
                      </span>
                      {!isEditing && (
                        <button
                          onClick={() => handleEdit(request)}
                          className="text-gray-400 hover:text-[#37b34a] transition-colors"
                        >
                          <Edit2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  {isEditing ? (
                    <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Budget Range</label>
                          <input
                            type="text"
                            value={editForm.budget_range}
                            onChange={(e) => setEditForm({ ...editForm, budget_range: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Urgency</label>
                          <select
                            value={editForm.urgency_level}
                            onChange={(e) => setEditForm({ ...editForm, urgency_level: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                          >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="urgent">Urgent</option>
                            <option value="emergency">Emergency</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={handleCancel}
                          className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSave(request.id)}
                          className="flex-1 bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-4 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                        >
                          <CheckCircle size={14} /> Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {request.description && (
                        <p className="text-sm text-gray-600 mb-3">{request.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                        {request.location && (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} /> {request.location}
                          </span>
                        )}
                        {request.budget_range && (
                          <span className="flex items-center gap-1">
                            <DollarSign size={12} /> {request.budget_range}
                          </span>
                        )}
                        <span className="capitalize">Urgency: {request.urgency_level || 'normal'}</span>
                      </div>
                    </>
                  )}

                  {/* Matched Professionals */}
                  {request.assigned_professionals && request.assigned_professionals.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        {request.assigned_professionals.length} professional(s) interested
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyRequests;
